import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { TORUN as TORUN_MEDIA } from "@/data/content";
import { useI18n } from "@/i18n";

export default function Torun() {
  const { t } = useI18n();
  const tor = t.torun;
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [lightbox]);

  const openImage = (src, alt) => setLightbox({ src, alt });

  return (
    <section
      id="torun"
      data-testid="torun-section"
      className="relative bg-canvas-soft py-14 md:py-20 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Intro */}
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-start mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-6">
            <Reveal>
              <p className="overline mb-6">{tor.overline}</p>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink">
                {tor.headingA} <span className="italic text-brand-red/80">{tor.headingB}</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 md:pt-2">
            <Reveal delay={0.15}>
              <p className="serif text-xl md:text-2xl font-light text-ink leading-snug mb-6 [text-wrap:pretty]">
                {tor.subtitle}
              </p>
              <p className="text-ink-muted text-base leading-relaxed [text-wrap:pretty]">
                {tor.body}
              </p>
            </Reveal>
          </div>
        </div>

        {/* Para zdjęć — miasto + pierniki */}
        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-16 md:mb-24">
          <StaggerItem>
            <button
              type="button"
              onClick={() => openImage(TORUN_MEDIA.images.town, tor.townAlt)}
              aria-label={`${t.common.zoom}: ${tor.townAlt}`}
              data-testid="torun-image-town"
              className="group block w-full aspect-[4/5] md:aspect-[3/4] overflow-hidden cursor-zoom-in"
            >
              <img
                src={TORUN_MEDIA.images.town}
                alt={tor.townAlt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                style={{ objectPosition: "center 30%" }}
              />
            </button>
          </StaggerItem>
          <StaggerItem>
            <button
              type="button"
              onClick={() => openImage(TORUN_MEDIA.images.piernik, tor.piernikAlt)}
              aria-label={`${t.common.zoom}: ${tor.piernikAlt}`}
              data-testid="torun-image-piernik"
              className="group block w-full aspect-[4/5] md:aspect-[3/4] overflow-hidden cursor-zoom-in"
            >
              <img
                src={TORUN_MEDIA.images.piernik}
                alt={tor.piernikAlt}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
              />
            </button>
          </StaggerItem>
        </Stagger>

        {/* Co warto zobaczyć — siatka numerowana */}
        <Reveal className="mb-12 md:mb-16 block">
          <div className="flex items-center gap-5">
            <span className="h-px w-12 bg-brand-red/60" />
            <h3 className="serif text-3xl md:text-4xl font-light text-ink leading-tight">
              {tor.spotsGroup}
            </h3>
          </div>
        </Reveal>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {tor.spots.map((s, i) => (
            <StaggerItem key={s.title}>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-brand-red text-xs tracking-[0.3em]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-px flex-1 bg-ink/20" />
              </div>
              <h4 className="serif text-2xl md:text-[1.6rem] font-light text-ink leading-tight">
                {s.title}
              </h4>
              <p className="mt-3 text-ink-muted text-sm leading-relaxed [text-wrap:pretty]">
                {s.desc}
              </p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="torun-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(null)}
          >
            <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm" />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              data-testid="torun-lightbox-close"
              aria-label="Zamknij"
              className="absolute top-6 right-6 z-10 text-canvas/80 hover:text-canvas transition-colors"
            >
              <X size={28} strokeWidth={1.4} />
            </button>
            <motion.img
              src={lightbox.src}
              alt={lightbox.alt}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 max-h-[90vh] max-w-[92vw] object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
